import type {
  FoodItem,
  FoodItemServingSize as ServingSize,
} from '@macro-tracker/models';
import type { Food as BackendFood, ServingSize as BackendFoodServingSize } from '@macro-tracker/models/validators';
import { foodsClient } from '../../../backend/src/client';
import { apiFetch } from './apiClient';
const client = foodsClient(apiFetch);

const extractLabelNutrients = (
  nutrition: unknown
): ServingSize['nutrition']['labelNutrients'] => {
  if (!nutrition || typeof nutrition !== 'object') {
    return undefined;
  }

  const maybeLabelNutrients = (nutrition as { labelNutrients?: unknown }).labelNutrients;
  if (!maybeLabelNutrients || typeof maybeLabelNutrients !== 'object') {
    return undefined;
  }

  return maybeLabelNutrients as ServingSize['nutrition']['labelNutrients'];
};

const toServingSize = (foodId: string, size: BackendFoodServingSize, index: number): ServingSize => ({
  id: `${index}`,
  label: size.name ?? '',
  grams: Number.isFinite(size.weight) && size.weight > 0 ? size.weight : null,
  nutrition: {
    calories: size.nutrition.calories,
    protein: size.nutrition.protein,
    carbs: size.nutrition.carbs,
    fat: size.nutrition.fat,
    labelNutrients: extractLabelNutrients(size.nutrition),
  },
  isWeightBased: size.isWeightBased ?? false,
});

const mapBackendFoodToFoodItem = (food: BackendFood): FoodItem => {
  return {
    id: food.id,
    name: food.name,
    brand: food.brand ?? undefined,
    servingSizes: food.servingSizes.map((size, index) => toServingSize(food.id, size, index)),
    isCustom: false,
    isProvisional: !food.isVerified,
    createdAt: food.createdAt,
    upc: food.upc,
  };
};

/**
 * Lookup food by UPC barcode using the complete workflow
 * 
 * @param upc - The UPC barcode to lookup
 * @returns Promise<BarcodeLookupResponse> if food found
 * @throws Promise<BarcodeNotFoundResponse> if food not found (404)
 * @throws Error for other failures
 */
export async function lookupFoodByBarcode(upc: string): Promise<{ food: FoodItem }> {
  const response = await client.barcode[':upc'].$get({ param: { upc: upc.trim() }});

  if (!response.ok) {
    if (response.status === 404) {
      // Food not found - return structured response
      const notFoundData = await response.json();
      throw new NotFoundError(notFoundData.message, notFoundData.allowCreateCustom);
    }

    // Handle other error cases
    const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
    throw new Error(errorData.message || `Barcode lookup failed: ${response.status}`);
  }

  //const data = (await response.json()) as { food: BackendFood; source: BarcodeLookupResponse['source'] };
  const data = await response.json();
  return {
    food: mapBackendFoodToFoodItem(data.food),
  };
}

/**
 * Custom error class for barcode not found cases
 */
export class NotFoundError extends Error {
  constructor(
    message: string,
    public readonly allowCreateCustom: boolean,
    public readonly isNotFound: boolean = true
  ) {
    super(message);
    this.name = 'NotFoundError';
  }
}

/**
 * Type guard to check if error is a NotFoundError
 */
export function isNotFoundError(error: unknown): error is NotFoundError {
  return error instanceof NotFoundError;
}
